import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-semibold ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 shadow-soft hover:shadow-card hover:-translate-y-0.5",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border-2 border-sage/30 bg-sage-light text-foreground hover:bg-sage-medium/30 hover:text-foreground",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80 shadow-soft",
        ghost: "hover:bg-secondary hover:text-secondary-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        // 16.09.2026 Lesbarkeit: Braun auf Blau erreichte nur 4,41:1. Jetzt Braun-Fläche
        // mit Creme-Text (7,82:1), Hover dreht auf Blau mit Braun-Text.
        cta: "bg-[#644B40] text-[#F3EFE8] hover:bg-[#7ECAEE] hover:text-[#644B40] shadow-card hover:shadow-elevated font-normal",
        hero: "bg-accent text-accent-foreground hover:bg-accent/90 shadow-elevated hover:-translate-y-1 text-base px-8 py-6",
        "hero-outline": "border-2 border-primary/30 bg-background/80 backdrop-blur-sm text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary text-base px-8 py-6",
        soft: "bg-sage-light text-primary hover:bg-sage-medium/30 font-semibold",
        warm: "bg-terracotta-light text-accent hover:bg-accent hover:text-accent-foreground",
        // ── Quiet Confidence (Customer-Cockpit redesign) ────────────
        // Use these in the customer-portal flow only. They map to the
        // green-brand primary + white/cream secondary from the
        // dashboard-redesign mockups. Marketing pages keep `cta` (orange).
        green:
          "bg-green-brand text-ink-900 border border-[#6FA634] hover:bg-[#7ECAEE] hover:-translate-y-0.5 hover:shadow-green-glow-hover",
        "green-outline":
          "bg-cream-soft text-ink-900 border border-hairline-strong hover:bg-cream-bg hover:border-ink-300",
        ink:
          "bg-ink-900 text-cream-bg border border-ink-900 hover:bg-ink-700 hover:-translate-y-0.5",
      },
      size: {
        default: "h-11 px-6 py-2",
        sm: "h-9 rounded-lg px-4",
        lg: "h-12 rounded-xl px-8 text-base",
        xl: "h-14 rounded-2xl px-10 text-lg",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
